import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Save, Share2, ShieldCheck, ShieldAlert, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { shareProfileToFacebook } from '@/utils/facebookShare';
import ImageUploader from '@/components/ImageUploader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import { getErrorMessage } from '@/utils/errorHandler';
const VendorProfile = () => {
  const [profile, setProfile] = useState({
    business_name: '',
    contact_phone: '',
    contact_email: '',
    bio: '',
    website: '',
    payment_methods: [],
    preferred_contact: 'email',
    verification_status: 'unverified',
    rejection_reason: null
  });
  const [verificationDoc, setVerificationDoc] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [docUploading, setDocUploading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendor/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setProfile({
        business_name: response.data.business_name || '',
        contact_phone: response.data.contact_phone || '',
        contact_email: response.data.contact_email || '',
        bio: response.data.bio || '',
        website: response.data.website || '',
        payment_methods: response.data.payment_methods || [],
        preferred_contact: response.data.preferred_contact || 'email',
        verification_status: response.data.verification_status || 'unverified',
        rejection_reason: response.data.rejection_reason
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/vendor/profile`,
        profile,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(getErrorMessage(error, 'Failed to update profile'));
    } finally {
      setSaving(false);
    }
  };

  const handleVerificationSubmit = async () => {
    if (verificationDoc.length === 0) {
      toast.error('Please upload a document');
      return;
    }
    
    setVerifying(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/vendor/verification/request`,
        null,
        {
          params: { document_url: verificationDoc[0] },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Verification request submitted');
      setProfile(prev => ({ ...prev, verification_status: 'pending' }));
    } catch (error) {
      console.error('Error requesting verification:', error);
      toast.error('Failed to submit verification request');
    } finally {
      setVerifying(false);
    }
  };

  const handlePaymentMethodToggle = (method) => {
    setProfile(prev => ({
      ...prev,
      payment_methods: prev.payment_methods.includes(method)
        ? prev.payment_methods.filter(m => m !== method)
        : [...prev.payment_methods, method]
    }));
  };

  const paymentOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'venmo', label: 'Venmo' },
    { value: 'other', label: 'Other' }
  ];

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-pulse">
          <User className="w-16 h-16 text-green-500 mx-auto mb-4" />
        </div>
        <p className="text-gray-600">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-6 h-6 text-green-600" />
            Vendor Profile
          </CardTitle>
          <CardDescription>
            Update your business information and contact details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Name */}
            <div className="space-y-2">
              <Label htmlFor="business_name">Business Name</Label>
              <Input
                id="business_name"
                value={profile.business_name}
                onChange={(e) => setProfile(prev => ({ ...prev, business_name: e.target.value }))}
                placeholder="Your Farm or Business Name"
                data-testid="business-name-input"
              />
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <Label htmlFor="bio">Bio / Description</Label>
              <Textarea
                id="bio"
                value={profile.bio}
                onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Tell customers about your business..."
                rows={4}
                data-testid="bio-input"
              />
            </div>

            {/* Contact Email */}
            <div className="space-y-2">
              <Label htmlFor="contact_email">Public Contact Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={profile.contact_email}
                onChange={(e) => setProfile(prev => ({ ...prev, contact_email: e.target.value }))}
                placeholder="contact@yourbusiness.com"
                data-testid="contact-email-input"
              />
              <p className="text-xs text-gray-500">Customers will see this email on your listings</p>
            </div>

            {/* Contact Phone */}
            <div className="space-y-2">
              <Label htmlFor="contact_phone">Contact Phone</Label>
              <Input
                id="contact_phone"
                type="tel"
                value={profile.contact_phone}
                onChange={(e) => setProfile(prev => ({ ...prev, contact_phone: e.target.value }))}
                placeholder="0412 345 678"
                data-testid="contact-phone-input"
              />
            </div>

            {/* Website */}
            <div className="space-y-2">
              <Label htmlFor="website">Website (Optional)</Label>
              <Input
                id="website"
                type="url"
                value={profile.website}
                onChange={(e) => setProfile(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://yourwebsite.com"
                data-testid="website-input"
              />
            </div>

            {/* Payment Methods */}
            <div className="space-y-2">
              <Label>Accepted Payment Methods</Label>
              <div className="grid grid-cols-2 gap-3">
                {paymentOptions.map(option => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`payment-${option.value}`}
                      checked={profile.payment_methods.includes(option.value)}
                      onChange={() => handlePaymentMethodToggle(option.value)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      data-testid={`payment-${option.value}`}
                    />
                    <Label htmlFor={`payment-${option.value}`} className="font-normal">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Preferred Contact Method */}
            <div className="space-y-2">
              <Label htmlFor="preferred_contact">Preferred Contact Method</Label>
              <Select
                value={profile.preferred_contact}
                onValueChange={(value) => setProfile(prev => ({ ...prev, preferred_contact: value }))}
              >
                <SelectTrigger id="preferred_contact" data-testid="preferred-contact-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="submit"
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={saving}
                data-testid="save-profile-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Profile'}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  shareProfileToFacebook(profile);
                  toast.success('Opening Facebook to share your vendor profile!');
                }}
                variant="outline"
                className="border-blue-600 text-blue-700 hover:bg-blue-50"
                data-testid="share-profile-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Profile
              </Button>
            </div>
          </form>

          {/* Verification Section */}
          <div className="mt-8 border-t pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              Identity Verification
            </h3>
            
            {profile.verification_status === 'verified' && (
              <Alert className="bg-green-50 border-green-200">
                <ShieldCheck className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-800">Verified Vendor</AlertTitle>
                <AlertDescription className="text-green-700">
                  Your identity has been verified. You have a verified badge on your profile.
                </AlertDescription>
              </Alert>
            )}

            {profile.verification_status === 'pending' && (
              <Alert className="bg-yellow-50 border-yellow-200">
                <Clock className="h-4 w-4 text-yellow-600" />
                <AlertTitle className="text-yellow-800">Verification Pending</AlertTitle>
                <AlertDescription className="text-yellow-700">
                  Your document is being reviewed by an admin.
                </AlertDescription>
              </Alert>
            )}

            {profile.verification_status === 'rejected' && (
              <Alert className="bg-red-50 border-red-200 mb-4">
                <ShieldAlert className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-800">Verification Rejected</AlertTitle>
                <AlertDescription className="text-red-700">
                  Reason: {profile.rejection_reason || 'Document verification failed.'}
                </AlertDescription>
              </Alert>
            )}

            {(profile.verification_status === 'unverified' || profile.verification_status === 'rejected') && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Upload a government-issued ID or business license to become a verified vendor.
                  Verified vendors gain more trust from the community.
                </p>
                <div className="space-y-2">
                  <Label>Upload Document</Label>
                  <ImageUploader
                    images={verificationDoc}
                    onImagesChange={setVerificationDoc}
                    maxImages={1}
                    onUploadStatusChange={setDocUploading}
                  />
                </div>
                <Button 
                  type="button" 
                  onClick={handleVerificationSubmit}
                  disabled={verifying || verificationDoc.length === 0 || docUploading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {verifying ? 'Submitting...' : (docUploading ? 'Uploading Document...' : 'Submit for Verification')}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorProfile;
